<script lang="ts">
  import { inview } from 'svelte-inview';
  import PaginatedLoader from '$lib/components/common/loaders/PaginatedLoader.svelte';
  import { getGalleries } from '$src/utils/api/gallery.util';
  import type { GalleryFiltersSchema, SortOption } from '$src/utils/api/feed.util';
  import GalleryCard from '$lib/components/gallery/GalleryCard.svelte';
  import { useScrollVersionController } from '../top-island';
  import { range } from 'lodash-es';
  import SkeletonGalleryCard from '$lib/components/common/skeletons/SkeletonGalleryCard.svelte';
  import  scrollToLastItemOnLoad  from '$lib/actions/scrollToLastItemOnLoad';
  import { writable } from 'svelte/store';
  import Arrow from '$icons/ArrowIcon.svelte';
  import { me, profileSelectedTab } from '$src/stores/user.store';
  import type { GalleryDocument } from 'garden-common';
  import { goto } from '$app/navigation';
  import CreateGallery from '$lib/components/Modals/gallery/CreateGallery.svelte';

  export let sort: SortOption;
  export let filters: GalleryFiltersSchema;

  let galleries: GalleryDocument[] = [];
  let createGallery: boolean = false;
  let fetchPage: () => Promise<void>;

  let failed = false;
  let exhausted = false;
  let loading = false;

  let paginatedLoader: PaginatedLoader<GalleryDocument>;

  function refresh() {
    if (!paginatedLoader) {
      return;
    }

    paginatedLoader.reset();
    paginatedLoader.fetchPage();
  }

  function handleGalleryCreated(evt: CustomEvent) {
    createGallery = false;
    goto('/gallery/' + evt.detail.gallery.title);
  }

  $: sort, filters, refresh();

  const galleriesStore = writable<typeof galleries>();

  $: $galleriesStore = galleries;

  const lastItemContainer = writable<HTMLElement>();
</script>

{#if createGallery}
  <CreateGallery
    post={null}
    on:ModalClose={() => {
      createGallery = false;
    }}
    on:GalleryCreated={handleGalleryCreated}
  />
{/if}

<PaginatedLoader
  bind:this={paginatedLoader}
  bind:items={galleries}
  bind:failed
  bind:exhausted
  bind:fetchPage
  bind:isLoading={loading}
  limit={6}
  fetchFunction={getGalleries}
  loadPageOnMount={false}
  getFetchFunctionOpts={({ page, limit }) => ({
    page,
    limit,
    sort,
    filters
  })}
/>

<div
  class="pt-4 overflow-auto"
  use:scrollToLastItemOnLoad={{ arrToWatch: galleriesStore, lastItemContainer }}
>
  <div class="lg:px-4 px-2 py-24 lg:py-4 rounded-2xl">
    <div class="flex gap-y-4 flex-col">
      <div class="w-full">
        <div class="w-full font-black flex flex-row justify-between uppercase">
          <span class="uppercase text-xl text-garden-grey-700 font-black"> Explore Galleries</span>
          <button
            class:hidden={!$me}
            on:click={() => {
              $profileSelectedTab = 'gallery';
              goto('/profile');
            }}
            class="text-brand-green flex items-center uppercase"
          >
            Saved <span class="-rotate-90"
              ><Arrow size="13" class="stroke-brand-green" strokeWidth="3" /></span
            >
          </button>
        </div>
      </div>

      <div class="grid lg:grid-cols-3 gap-4">
        {#each galleries as gallery}
          <div bind:this={$lastItemContainer}>
            <GalleryCard {gallery} variant="vertical" />
          </div>
        {/each}
      </div>

      {#key galleries}
        {#if !exhausted}
          <div use:inview on:inview_enter={fetchPage} class="grid lg:grid-cols-3 gap-4">
            {#each range(9) as _}
              <SkeletonGalleryCard />
            {/each}
          </div>
        {/if}
      {/key}

      <div class="font-bold text-center w-full text-gray-400 mt-8 h-80">
        {#if failed}
          Load failed.
        {:else if exhausted}
          No more galleries to show.
        {:else}
          Loading...
        {/if}
      </div>
    </div>
  </div>
</div>
